// auth-gate.tsx — mandatory sign-in wall for cloud-sync builds.
// Until the user is authenticated, NOTHING of the app (not even demo/seed data)
// is rendered — fixes "open the link and see data without logging in". Session is
// persisted by Supabase, so a returning user is not asked to log in again.
//
// Local-only builds (cloudSyncEnabled=false) have no accounts → gate is bypassed.
import { useState } from "react";
import { ArrowRight, Bot, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import type { LifeOsApi } from "../lib/life-os-contract";
import { AiError } from "../lib/ai/ai-client";

function errorText(err: unknown): string {
  if (err instanceof AiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return "Đã có lỗi xảy ra. Vui lòng thử lại.";
}

export function AuthGate({ api, children }: { api: LifeOsApi; children: ReactNode }) {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [notice, setNotice] = useState<{ kind: "info" | "error"; text: string } | null>(null);

  // Local-only build, or already signed in → show the real app.
  if (!api.cloudSyncEnabled || api.auth.user) return <>{children}</>;

  // Session may still be restoring on reload — don't flash the login wall.
  if (api.syncState === "connecting") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 text-ink-500 dark:bg-[rgb(var(--surface))]">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-[14px] font-semibold">Đang kết nối…</span>
      </div>
    );
  }

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const form = new FormData(e.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    if (!email || !password) return;
    setBusy(true);
    setNotice(null);
    const req = mode === "signin" ? api.auth.signIn(email, password) : api.auth.signUp(email, password);
    req
      .then((data) => {
        // signIn → session set → gate re-renders into the app automatically.
        // signUp with "Confirm email" ON → user but NO session → tell them to verify.
        const session = (data as { session?: unknown } | null | undefined)?.session;
        if (mode === "signup" && !session) {
          setNotice({ kind: "info", text: "Đã gửi email xác nhận. Mở email, bấm liên kết kích hoạt rồi đăng nhập." });
        }
      })
      .catch((err) => setNotice({ kind: "error", text: errorText(err) }))
      .finally(() => setBusy(false));
  };

  const forgot = () => {
    const email = window.prompt("Nhập email để nhận liên kết đặt lại mật khẩu:");
    if (!email) return;
    api.auth
      .resetPassword(email.trim())
      .then(() => setNotice({ kind: "info", text: "Đã gửi email đặt lại mật khẩu." }))
      .catch((err) => setNotice({ kind: "error", text: errorText(err) }));
  };

  const tab = (m: "signin" | "signup", label: string) => (
    <button
      type="button"
      onClick={() => { setMode(m); setNotice(null); }}
      className={`rounded-lg py-2 text-[13px] font-bold transition ${
        mode === m ? "bg-white text-brand-700 shadow-sm dark:bg-white/10 dark:text-white" : "text-ink-500 hover:text-ink-700"
      }`}
    >
      {label}
    </button>
  );

  const field = "w-full rounded-xl border border-ink-200 bg-ink-50/60 py-3 pl-10 text-[14px] text-ink-900 outline-none transition placeholder:text-ink-400 focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-100/70 dark:bg-white/5 dark:focus:bg-white/10";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-brand-50 via-white to-ink-50 px-4 dark:from-brand-500/10 dark:via-[rgb(var(--surface))] dark:to-[rgb(var(--surface))]">
      {/* Soft decorative glow — purely cosmetic, never intercepts clicks */}
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-brand-300/30 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-brand-400/20 blur-3xl" />

      <div className="relative w-full max-w-[380px] rounded-[28px] border border-white/70 bg-white/95 p-8 shadow-[0_30px_90px_rgba(31,29,43,0.20)] backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
        {/* Brand */}
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-[0_10px_30px_rgba(124,58,237,0.35)]">
            <Bot className="h-7 w-7" />
          </span>
          <div className="space-y-0.5">
            <h1 className="font-display text-[20px] font-bold tracking-tight text-ink-900">Personal Life OS</h1>
            <p className="text-[13px] text-ink-500">
              {mode === "signin" ? "Đăng nhập để tiếp tục" : "Tạo tài khoản mới"}
            </p>
          </div>
        </div>

        {/* Segmented mode switch */}
        <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-ink-100 p-1 dark:bg-white/5">
          {tab("signin", "Đăng nhập")}
          {tab("signup", "Đăng ký")}
        </div>

        <form className="space-y-2.5" onSubmit={submit}>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input name="email" type="email" required autoComplete="email" placeholder="Email" className={field} />
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input
              name="password"
              type={showPw ? "text" : "password"}
              required
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              placeholder="Mật khẩu"
              className={`${field} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-ink-400 transition hover:bg-ink-100 hover:text-ink-600"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {mode === "signin" && (
            <div className="flex justify-end">
              <button type="button" onClick={forgot} className="text-[12px] font-semibold text-ink-500 transition hover:text-brand-600">
                Quên mật khẩu?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="gbrand mt-1 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[14px] font-bold text-white shadow-[0_8px_24px_rgba(124,58,237,0.35)] transition hover:opacity-95 active:scale-[0.99] disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {mode === "signin" ? "Đăng nhập" : "Đăng ký"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>

        {notice && (
          <p
            className={`mt-4 rounded-xl px-3.5 py-2.5 text-[12.5px] font-medium ${
              notice.kind === "error"
                ? "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"
                : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
            }`}
          >
            {notice.text}
          </p>
        )}

        <p className="mt-6 text-center text-[11px] leading-relaxed text-ink-400">
          Mỗi tài khoản có dữ liệu riêng, không chia sẻ.<br />Phiên đăng nhập được ghi nhớ trên thiết bị này.
        </p>
      </div>
    </div>
  );
}
